//////////////////////////////////////////////////////////
//////////////// BROWSER DATA COLLECTING /////////////////
//////////////////////////////////////////////////////////

type BrowserData = {
  colorDepth: string;
  screenHeight: number;
  screenWidth: number;
  userAgent: string;
  javaEnabled: boolean;
  language: string;
  timeZone: number;
  javascriptEnabled: boolean;
  acceptHeader: string;
};

// 3DS accepts only certain color depths based on 3ds-client.ts (library provided by Akua)
const VALID_COLOR_DEPTHS = [1, 4, 8, 15, 16, 24, 32, 48] as const;
function getValidColorDepth(depth: number) {
  if (VALID_COLOR_DEPTHS.includes(depth as (typeof VALID_COLOR_DEPTHS)[number])) return String(depth);
  return '24';
}

/**
 * Collects browser environment data for 3DS. Client-side only (uses window/navigator/screen).
 *
 * Returns null instead of a partial object when the environment cannot supply these.
 * The backend rejects a card payment with incomplete browser data rather than filling
 * the gaps in — inventing a device profile is what let a caller opt out of 3DS — so a
 * partial payload would only fail later and with a worse message.
 */
// Function adapted from 3ds-client.ts (library provided by Akua)
export function getBrowserData(): BrowserData | null {
  if (typeof window === 'undefined' || !window.screen || !window.navigator) return null;

  return {
    colorDepth: getValidColorDepth(window.screen.colorDepth ?? 24),
    screenHeight: window.screen.height,
    screenWidth: window.screen.width,
    userAgent: window.navigator.userAgent,
    language:
      navigator.language ||
      (navigator as unknown as { browserLanguage?: string }).browserLanguage ||
      (navigator as unknown as { userLanguage?: string }).userLanguage ||
      'es-CO',
    timeZone: new Date().getTimezoneOffset(),
    javaEnabled: false,
    javascriptEnabled: true,
    acceptHeader: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
  };
}

/**
 * Decodes HTML entities (e.g. `&lt;` → `<`) in a string.
 * Used to decode Wompi's `three_ds_method_data` before injecting as innerHTML.
 * Browser-only — safe because DOMParser does not execute scripts.
 */
export function decodeHtmlEntities(encoded: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(encoded, 'text/html');
  return doc.body.textContent ?? '';
}
